import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getPendingFavoriteSetlistId } from '../../services/SetlistStorageService';
import logger from '../../services/LoggingService';

/**
 * Simplified Login and Registration component
 * Rebuilt to avoid event propagation issues
 */
const LoginRegister = ({ onClose }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  
  // Use the auth context
  const { login, register, error: authError } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setLocalError('');
    
    // Check if there's a pending favorite before login using our service method
    const pendingSetlistIdBefore = getPendingFavoriteSetlistId();
    logger.debug('LoginRegister', 'LOGIN ATTEMPT: Pending favorite setlist before login:', pendingSetlistIdBefore);
    
    try {
      let success;
      
      if (isLogin) {
        console.log('LOGIN ATTEMPT: Attempting login...');
        success = await login(email, password);
      } else {
        console.log('LOGIN ATTEMPT: Attempting registration...');
        success = await register(username, email, password);
      }
      
      if (success) {
        logger.debug('LoginRegister', 'LOGIN SUCCESS: Authentication successful');
        
        // Check if there's a pending favorite after login using our service method
        const pendingSetlistId = getPendingFavoriteSetlistId();
        logger.debug('LoginRegister', 'LOGIN SUCCESS: Pending favorite setlist after login:', pendingSetlistId);
        
        // Close the modal
        if (onClose) {
          logger.debug('LoginRegister', 'LOGIN SUCCESS: Closing auth modal');
          onClose();
        }
      } else {
        console.log('LOGIN FAILURE: Authentication failed');
      }
    } catch (err) {
      console.error('LOGIN ERROR: Authentication error:', err);
      setLocalError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  // Simple handler for input changes
  const handleChange = (setter) => (e) => {
    setter(e.target.value);
  };

  // Toggle between login and register forms
  const toggleForm = () => {
    setIsLogin(!isLogin);
    setLocalError('');
  };

  const error = localError || authError;

  return (
    <div className="w-full max-w-md">
      <h2 className="text-2xl font-bold mb-6 text-center">
        {isLogin ? 'Sign In' : 'Create Account'}
      </h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        {!isLogin && (
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
              Username
            </label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => {
                // Convert to lowercase and remove spaces as user types
                const value = e.target.value.toLowerCase().replace(/\s/g, '');
                setUsername(value);
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900"
              required
              autoComplete="username"
              pattern="[a-z0-9_-]{3,20}"
              title="Username must be 3-20 characters, lowercase letters, numbers, underscores, or hyphens only"
            />
            <p className="mt-1 text-xs text-gray-500">
              Lowercase only, no spaces (e.g., "funkdaddy" or "john_doe")
            </p>
          </div>
        )}
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <input
            type="email"
            id="email"
            value={email}
            onChange={handleChange(setEmail)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900"
            required
            autoComplete="email"
          />
        </div>
        
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={handleChange(setPassword)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-gray-900"
            required
            autoComplete={isLogin ? "current-password" : "new-password"}
          />
        </div>
        
        <div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </div>
      </form>
      
      <div className="mt-4 text-center">
        <button
          type="button"
          onClick={toggleForm}
          className="text-sm text-blue-600 hover:text-blue-500"
        >
          {isLogin ? 'Need an account? Register' : 'Already have an account? Sign in'}
        </button>
      </div>
    </div>
  );
};

export default LoginRegister;
